import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  BookOpen, 
  GraduationCap, 
  CheckCircle, 
  Clock, 
  UserCheck,
  Building,
  Calendar,
  MapPin,
  Mail,
  Phone
} from 'lucide-react';

interface DrillDownDialogProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'users' | 'courses' | 'enrollments' | 'completions' | 'proposals' | 'instructors';
  data: any[];
  title: string;
}

export default function DrillDownDialog({ 
  isOpen, 
  onClose, 
  type, 
  data, 
  title 
}: DrillDownDialogProps) {
  const renderContent = () => {
    switch (type) {
      case 'users':
        return (
          <div className="grid gap-4">
            {data.map((user, index) => (
              <Card key={index} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-medium">{user.first_name} {user.last_name}</h4>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {user.email}
                      </p>
                      {user.phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {user.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline">{user.department}</Badge>
                    <p className="text-sm text-muted-foreground mt-1">{user.position_title}</p>
                    {user.school_name && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Building className="h-3 w-3" />
                        {user.school_name}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        );

      case 'courses':
        return (
          <div className="grid gap-4">
            {data.map((course, index) => (
              <Card key={index} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-medium">{course.title}</h4>
                      <p className="text-sm text-muted-foreground">{course.description}</p>
                      {course.location && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          {course.location}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={course.status === 'published' ? 'default' : 'outline'}>
                      {course.status}
                    </Badge>
                    <p className="text-sm text-muted-foreground mt-1">
                      {course.duration_hours} hours
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Max: {course.max_participants || 'Unlimited'} participants
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        );

      case 'enrollments':
        return (
          <div className="grid gap-4">
            {data.map((enrollment, index) => (
              <Card key={index} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <GraduationCap className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-medium">Enrollment #{enrollment.id.slice(0, 8)}</h4>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(enrollment.enrollment_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={enrollment.status === 'enrolled' ? 'default' : 'outline'}>
                      {enrollment.status}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      Type: {enrollment.enrollment_type}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        );

      case 'completions':
        return (
          <div className="grid gap-4">
            {data.map((completion, index) => (
              <Card key={index} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-success" />
                    <div>
                      <h4 className="font-medium">Completion #{completion.id.slice(0, 8)}</h4>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {completion.completion_date ? 
                          new Date(completion.completion_date).toLocaleDateString() : 
                          'Not completed'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="default">Completed</Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      Hours: {completion.attendance_hours || 0}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        );

      case 'proposals':
        return (
          <div className="grid gap-4">
            {data.map((proposal, index) => (
              <Card key={index} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-warning" />
                    <div>
                      <h4 className="font-medium">{proposal.title}</h4>
                      <p className="text-sm text-muted-foreground">{proposal.proposer_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(proposal.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={proposal.status === 'pending' ? 'outline' : 'default'}>
                      {proposal.status}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {proposal.duration_hours} hours
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        );

      case 'instructors':
        return (
          <div className="grid gap-4">
            {data.map((instructor, index) => (
              <Card key={index} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <UserCheck className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-medium">{instructor.first_name} {instructor.last_name}</h4>
                      <p className="text-sm text-muted-foreground">{instructor.email}</p>
                      <p className="text-xs text-muted-foreground">{instructor.department}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="default">Instructor</Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {instructor.school_name}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        );

      default:
        return <p>No data available</p>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Detailed view of {data.length} {type}
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4">
          {renderContent()}
        </div>

        <div className="flex justify-end mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}